// src/lib/api.js

// ===================================================================================
// CONFIGURACIÓN
// ===================================================================================
export const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// ===================================================================================
// GESTIÓN DEL TOKEN CSRF
// ===================================================================================
// El backend emite el token CSRF como cookie NO-httpOnly (issueCsrfToken) tras el
// login (usuario o admin). El frontend debe leerlo de la cookie y reenviarlo en el
// header 'x-csrf-token' en cada petición que modifique datos (POST/PUT/PATCH/DELETE).

let cachedCsrfToken = null;

/**
 * Lee el token CSRF actual desde la cookie 'csrf_token'.
 */
function readCsrfTokenFromCookie() {
  const match = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]*)/);
  return match ? decodeURIComponent(match[1]) : null;
}

/**
 * Permite fijar manualmente el token en memoria (por si el backend lo devuelve
 * también en el body de alguna respuesta, además de la cookie).
 */
export function setCsrfToken(token) {
  cachedCsrfToken = token;
}

/**
 * Devuelve el token CSRF a usar: prioriza la cookie (siempre la fuente de verdad),
 * y cae a la caché en memoria como respaldo.
 */
export function getCsrfToken() {
  const fromCookie = readCsrfTokenFromCookie();
  if (fromCookie) {
    cachedCsrfToken = fromCookie;
    return fromCookie;
  }
  return cachedCsrfToken;
}

/**
 * Fuerza una relectura del token CSRF desde la cookie (útil justo después de un
 * login, cuando el backend acaba de emitir la cookie nueva).
 */
export function refrescarCsrfToken() {
  const token = readCsrfTokenFromCookie();
  cachedCsrfToken = token;
  return token;
}

// ===================================================================================
// WRAPPER DE FETCH
// ===================================================================================
const MUTATING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

/**
 * apiFetch(path, options)
 * - Antepone API_BASE_URL si 'path' es relativo (empieza por '/').
 * - Añade credentials: 'include' siempre (para que viajen las cookies httpOnly de sesión).
 * - Añade automáticamente el header 'x-csrf-token' en métodos que mutan datos.
 * - Si el body es un objeto plano (no FormData), lo serializa a JSON y añade el header
 *   Content-Type correspondiente.
 * - Lanza un Error con el mensaje del backend si la respuesta no es 2xx (o un mensaje
 *   genérico si el backend no devolvió JSON válido).
 *
 * Devuelve directamente el JSON parseado de la respuesta (o null si no hay body).
 */
export async function apiFetch(path, options = {}) {
  const url = path.startsWith('http') ? path : `${API_BASE_URL}${path}`;
  const method = (options.method || 'GET').toUpperCase();

  const headers = { ...(options.headers || {}) };
  let body = options.body;

  const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;

  if (body && !isFormData && typeof body === 'object') {
    body = JSON.stringify(body);
    if (!headers['Content-Type']) headers['Content-Type'] = 'application/json';
  }

  if (MUTATING_METHODS.has(method)) {
    const token = getCsrfToken();
    if (token) headers['x-csrf-token'] = token;
  }

  const response = await fetch(url, {
    ...options,
    method,
    headers,
    body,
    credentials: 'include',
  });

  let data = null;
  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    try {
      data = await response.json();
    } catch {
      data = null;
    }
  }

  if (!response.ok) {
    const message = data?.error || `Error ${response.status} al llamar a ${path}`;
    const error = new Error(message);
    error.status = response.status;
    error.data = data;
    throw error;
  }

  return data;
}